// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardTileHeader.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { propTypes as ObservablePropTypes } from 'mobx-react';
import connect from 'share/enhancers/Izenda.Connect';
import { isNil, map, includes, isEmpty } from 'lodash/fp';

import PreviewRecord from './Header/Izenda.PreviewRecord';
import FlipButton from './Header/Izenda.FlipButton';
import FullScreenButton from './Header/Izenda.FullScreenButton';
import RefreshButton from './Header/Izenda.RefreshButton';
import DeleteButton from './Header/Izenda.DeleteButton';
import CopyButton from './Header/Izenda.CopyButton';
import NameInput from './Header/Izenda.NameInput';
import UiState from 'logic/Izenda.UiState';
import MenuContext from 'share/components/controls/Izenda.MenuContext';
import { DASHBOARD_PART_MIN_WIDTH } from 'logic/data/Izenda.DashboardConstants';
import { NO_PERMISSION } from 'logic/data/Izenda.ReportsConstants';

@connect()
export default class DashboardTileHeader extends Component {
  static propTypes = {
    store: ObservablePropTypes.observableObject.isRequired,
    dashboardDetail: ObservablePropTypes.observableObject.isRequired,
    dashboardPart: ObservablePropTypes.observableObject.isRequired,
    viewMode: PropTypes.bool
  };

  onUpdateData = () => {
    const { store, loader, dashboardPart } = this.props;

    loader.startLoading();
    store.getReportPartData(dashboardPart).finally(loader.endLoading.bind(loader));
  };

  getListControl = () => {
    const {
      editDashboard,
      dashboardPart,
      viewMode,
      dashboardDetail,
      store,
      dashboardPart: { disableViewReport }
    } = this.props;
    const refreshButton = (
      <RefreshButton key='refreshBtn' dashboardPart={dashboardPart} onUpdateData={this.onUpdateData} />
    );
    const fullScreenButton = <FullScreenButton key='fsBtn' dashboardPart={dashboardPart} />;

    if (!editDashboard) return [refreshButton, fullScreenButton];

    const isEditable = !isNil(dashboardPart.type) && !viewMode && !disableViewReport;
    const copyButton = <CopyButton key='copyBtn' store={store} dashboardPart={dashboardPart} />;
    const flipButton = <FlipButton key='flipBtn' dashboardPart={dashboardPart} dashboardDetail={dashboardDetail} />;
    const deleteButton = <DeleteButton key='delBtn' store={store} dashboardPart={dashboardPart} viewMode={viewMode} />;

    return isEditable ? [refreshButton, copyButton, fullScreenButton, flipButton, deleteButton] : [deleteButton];
  };

  renderContextMenu = (buttons: Array<any>) => {
    return (
      <div className='izenda-ReportPartHeader_ContextMenu'>
        <ul className='izenda-ContextMenu-list'>
          {map(button => <li key={button.key}>{cloneElement(button, { showLabel: true })}</li>, buttons)}
        </ul>
      </div>
    );
  };

  render() {
    const props = this.props;
    const {
      editDashboard,
      store,
      viewMode,
      presentationMode,
      dashboardPart,
      dashboardDetail,
      dashboardPart: { disableViewReport, disableViewReportMessageKey }
    } = props;
    const isSelectedReportPartType = !isNil(dashboardPart.type);
    const isEditable = isSelectedReportPartType && !viewMode && !disableViewReport;
    const buttons = this.getListControl();

    const noPermission = (isNil(disableViewReportMessageKey) && isEmpty(disableViewReportMessageKey)) ? false
                                  : ( disableViewReportMessageKey === NO_PERMISSION);

    const className = classnames('draggableBox u_fullWidth izenda-DashboardTile-Content-Header u_overflowHidden', {
      'u-posAbsolute': !viewMode,
      'izenda-DashboardTile-Content-Header-slid': !viewMode,
      'izenda-DashboardTile-Content-Header-fixed': viewMode && !store.dashboardDesignerMode.isFullscreen,
      'izenda-ReportPartHeader-content': true,
      'small-size':
        dashboardPart.width < DASHBOARD_PART_MIN_WIDTH &&
        isSelectedReportPartType &&
        !dashboardPart.isBackSide &&
        !dashboardPart.isFullScreenMode &&
        !presentationMode &&
        !UiState.isMobile
    });

    return (
      <MenuContext
        id={dashboardPart.id}
        isDisabled={!isSelectedReportPartType}
        menuComponent={this.renderContextMenu(buttons)}
      >
        <div className={className} aria-selected={isSelectedReportPartType}>
          {!UiState.isMobile && <div className='izenda-ReportPartHeader-buttonAction'>{buttons}</div>}

          <div className='izenda-ReportPartHeader-title'>
            {isSelectedReportPartType &&
              (!noPermission) && (
                <NameInput
                  disabled={!editDashboard}
                  dashboardDetail={dashboardDetail}
                  dashboardPart={dashboardPart}
                  viewMode={viewMode}
                />
              )}
          </div>

          {!UiState.isMobile && (
            <div className='izenda-ReportPartHeader-record'>
              {isEditable && (
                <PreviewRecord key='previewRecord' dashboardPart={dashboardPart} onUpdateData={this.onUpdateData} />
              )}
            </div>
          )}
        </div>
      </MenuContext>
    );
  }
}


// WEBPACK FOOTER //
// ./ui/modules/Dashboard/DashboardDesigner/DashboardGrid/DashboardTile/Izenda.DashboardTileHeader.js